/**
 * 语音识别「中国侧中继」客户端
 *
 * ── 它解决的问题 ──────────────────────────────────────────────
 * API 在 Zeabur 美东，识别引擎在阿里云杭州。一次识别要绕：
 *
 *   手机 → 美东 → 杭州 → 美东 → 手机        实测 5.7~8.5 秒
 *   手机 → 上海 → 杭州 → 上海 → 手机        实测 0.53~1.09 秒（同一段录音）
 *
 * 后者是同一份识别逻辑跑在腾讯云函数 `voice-relay`（上海），
 * 识别结果逐字一致（「老板这个菜咋个卖。」）。
 *
 * ── 怎么接上的（换环境时要照着来）──────────────────────────────
 * 云函数对外有两条通道，这里用**旧版 HTTP 访问服务**：
 *
 *   tcb service create -p voice -f voice-relay -e <envId>
 *   → https://<envId>-<appid>.ap-shanghai.app.tcloudbase.com/voice
 *
 * ⚠️ 另一条 `tcb routes add`（新版 HTTP 网关）**只认自定义域名**，
 *    系统域名一律被拒（system internal domain）。而 `tcb service create`
 *    可以直接用默认域名 —— 不需要备案、不需要 SDK、不需要匿名登录。
 *    这两条命令看着像一回事，实际完全不是，别再走错。
 *
 * ── 三条必须守住的规矩 ─────────────────────────────────────────
 * 1. **永远可以失败**。没配地址、超时、体积超限、函数报错……
 *    任何情况都返回 null，让调用方退回 /api/recognize。
 *    识别是学习链路第一环，不能为了快承担不可用的风险。
 * 2. **体积有硬上限**。HTTP 访问服务的请求体上限约 100KB
 *    （实测 99KB 通过、120KB 报 EXCEED_MAX_PAYLOAD_SIZE），
 *    而音频要 base64（+33%）。这里留 90KB 余量，超了直接走老路 ——
 *    否则会拿到一个 413，白白多等一次往返。
 * 3. **粤语必须指定腾讯引擎**。粤语识别是"腾讯优先"（见 app.js 的
 *    recognizeByDialect），而且这不是快慢问题，是**算分问题**：
 *    腾讯返回简化字后用 toTraditional 归一成港式繁体（唔該/凍奶茶），
 *    千问会做用字归一（唔该/冻奶茶/**少甜**）。粤语过关判定是按字比对，
 *    用千问等于让学生读对了也扣分。所以粤语走 `engine: 'tencent'`。
 *    腾讯不支持 webm —— 那种录音直接退回主服务，不要硬送。
 */

const RELAY_URL = String(import.meta.env.VITE_VOICE_RELAY_URL || '').replace(/\/$/, '');
const TOKEN = import.meta.env.VITE_VOICE_RELAY_TOKEN || '';

/** 中继整体超时：超过就回退，不让学生干等 */
const RELAY_TIMEOUT_MS = 8000;

/**
 * base64 后的请求体上限（保守值）
 *
 * 服务端上限约 100KB，这里留 10KB 给 JSON 外壳。
 * 16kbps 的 m4a：5 秒≈10KB、30 秒≈60KB，都装得下；
 * 只有浏览器回退到 wav 这类大格式时才可能超（那时走老路，功能不受影响）。
 */
const MAX_BASE64_BYTES = 90 * 1024;

/** 确认这条路不可用后，本场不再尝试（避免每次白等一次超时） */
let relayDead = false;

/**
 * 这个方言能不能走中继
 *
 * URL / TOKEN 没配、或本场已判定不可用 → 不走。
 *
 * @param {string} dialectId
 * @returns {boolean}
 */
export function shouldUseRelay(dialectId) {
  if (!RELAY_URL || !TOKEN || relayDead) return false;
  return true;
}

/**
 * 这个方言该用哪个识别引擎
 *
 * 与 app.js 的 recognizeByDialect 保持同一套判断：
 *   · 粤语 → 腾讯（用字正确是判分前提）
 *   · 其它 → 千问
 *
 * @param {string} dialectId
 * @returns {'tencent'|'qwen'}
 */
export function engineFor(dialectId) {
  return dialectId === 'yue-HK' ? 'tencent' : 'qwen';
}

function mimeToFormat(mime = '') {
  const m = String(mime).split(';')[0].trim().toLowerCase();
  if (m.includes('mp4') || m.includes('m4a')) return 'mp4';
  if (m.includes('webm')) return 'webm';
  if (m.includes('ogg')) return 'ogg';
  if (m.includes('mpeg') || m.includes('mp3')) return 'mp3';
  if (m.includes('wav')) return 'wav';
  return 'wav';
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      resolve(s.slice(s.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('读取录音失败'));
    reader.readAsDataURL(blob);
  });
}

/**
 * 走中继识别
 *
 * @param {Blob} blob 录音
 * @param {string} [dialectId] 仅用于判断能否走中继（识别模型本身与方言无关）
 * @returns {Promise<{text:string, provider:string}|null>} 失败一律返回 null
 */
export async function recognizeViaRelay(blob, dialectId) {
  if (!blob || !shouldUseRelay(dialectId)) return null;

  try {
    const engine = engineFor(dialectId);

    /*
     * 腾讯一句话识别不支持 webm（主服务 services/asr.js 的 MIME 表里
     * webm 是 null）。遇到就直接走主服务，别让中继白跑一次再报错。
     */
    if (engine === 'tencent' && /webm/i.test(blob.type || '')) {
      console.info('[voice-relay] 腾讯不支持 webm，改走主服务识别');
      return null;
    }

    const audio = await blobToBase64(blob);
    if (audio.length > MAX_BASE64_BYTES) {
      // 太长就走主服务：那边是 multipart，不受这个 100KB 限制
      console.info('[voice-relay] 音频过大，改走主服务识别', Math.round(audio.length / 1024) + 'KB');
      return null;
    }

    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), RELAY_TIMEOUT_MS) : null;

    let resp;
    try {
      resp = await fetch(`${RELAY_URL}/asr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-relay-token': TOKEN },
        body: JSON.stringify({
          audio,
          mime: blob.type,
          format: mimeToFormat(blob.type),
          engine,
        }),
        ...(ctrl ? { signal: ctrl.signal } : {}),
      });
    } finally {
      if (timer) clearTimeout(timer);
    }

    if (!resp.ok) {
      console.warn('[voice-relay] 返回', resp.status, '，退回主服务');
      return null;
    }

    const data = await resp.json();
    if (!data?.ok) {
      console.warn('[voice-relay] 识别失败，退回主服务：', data?.error || 'unknown');
      return null;
    }
    const text = String(data.text || '').trim();
    return text ? { text, provider: data.provider || 'qwen' } : null;
  } catch (e) {
    /*
     * 网络错误 / 超时 / 被墙 —— 退回主服务。
     * 单次失败不判死这条路：可能只是这一句触发了慢路径。
     */
    console.warn('[voice-relay] 调用异常，退回主服务：', e?.message || e);
    return null;
  }
}

/** 供排查用：这条链路当前是死的还是活的 */
export function relayStatus() {
  return { enabled: !!RELAY_URL && !!TOKEN, dead: relayDead, url: RELAY_URL };
}
